import { useMutation, useApolloClient } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { PatternFormat } from "react-number-format";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { graphql } from "@/graphql";
import { useToast } from "@/hooks/use-toast";
import { extractErrorMessages } from "@/lib/utils/errors";
import { transformForApi } from "@/lib/utils/transforms";
import {
  createRequiredStringSchema,
  createOptionalStringSchema,
  createOptionalPhoneSchema,
} from "@/lib/utils/validation/zod";

const profileEditSchema = z.object({
  firstName: createRequiredStringSchema("first name", 50),
  lastName: createRequiredStringSchema("last name", 50),
  jobTitle: createRequiredStringSchema("job title", 100),
  about: createOptionalStringSchema("about", 500),
  phone: createOptionalPhoneSchema(),
});

type ProfileEditForm = z.infer<typeof profileEditSchema>;

interface UserProfileEditFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function UserProfileEditForm({ open, onOpenChange, onSuccess }: UserProfileEditFormProps) {
  const { toast } = useToast();
  const client = useApolloClient();

  const form = useForm<ProfileEditForm>({
    resolver: zodResolver(profileEditSchema),
    defaultValues: async () => {
      try {
        const { data } = await client.query({
          query: userProfileForEditQuery,
          fetchPolicy: "cache-first",
        });

        const user = data?.currentUser;

        if (!user) {
          toast({
            title: "Error loading profile",
            description: "No user data found. Please try again.",
            variant: "destructive",
          });

          return {
            firstName: "",
            lastName: "",
            jobTitle: "",
            about: "",
            phone: "",
          };
        }

        return {
          firstName: user.firstName || "",
          lastName: user.lastName || "",
          jobTitle: user.jobTitle || "",
          about: user.about || "",
          phone: user.phone || "",
        };
      } catch {
        toast({
          title: "Error loading profile",
          description: "Failed to load profile data for editing. Please try again.",
          variant: "destructive",
        });

        return {
          firstName: "",
          lastName: "",
          jobTitle: "",
          about: "",
          phone: "",
        };
      }
    },
  });

  const [updateUser, { loading }] = useMutation(updateCurrentUserMutation);

  const onSubmit = async (data: ProfileEditForm) => {
    try {
      const result = await updateUser({
        variables: {
          input: {
            firstName: transformForApi.text(data.firstName),
            lastName: transformForApi.text(data.lastName),
            jobTitle: transformForApi.text(data.jobTitle),
            about: data.about ? data.about.trim() : undefined,
            phone: data.phone,
          },
        },
      });

      const errors = result.data?.updateUserInfo?.errors || [];

      if (errors.length > 0) {
        toast({
          title: "Failed to update profile",
          description: extractErrorMessages(errors),
          variant: "destructive",
        });
        return;
      }

      if (result.data?.updateUserInfo?.currentUser) {
        toast({
          title: "Profile updated",
          description: "Your profile has been successfully updated.",
        });
        onOpenChange(false);
        onSuccess?.();
      }
    } catch (error) {
      toast({
        title: "Failed to update profile",
        description: extractErrorMessages(error),
        variant: "destructive",
      });
    }
  };

  const handleClose = () => {
    form.reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Profile</DialogTitle>
          <DialogDescription>Update your personal information and contact details.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-2">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First Name *</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter first name" {...field} />
                    </FormControl>
                    <div className="min-h-[20px]">
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last Name *</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter last name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="jobTitle"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Job Title *</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter job title" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone</FormLabel>
                  <FormControl>
                    <PatternFormat
                      customInput={Input}
                      getInputRef={field.ref}
                      format="(###) ###-####"
                      mask="_"
                      allowEmptyFormatting={false}
                      type="tel"
                      placeholder="(555) 123-4567"
                      value={field.value}
                      onValueChange={(values) => {
                        field.onChange(values.value);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="about"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>About</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Enter about information..." className="resize-none" rows={3} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading} className="gap-2">
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                Save
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

const userProfileForEditQuery = graphql(`
  query UserProfileForEdit {
    currentUser {
      id
      firstName
      lastName
      jobTitle
      about
      phone
    }
  }
`);

export const updateCurrentUserMutation = graphql(`
  mutation UpdateCurrentUser($input: UpdateUserInput!) {
    updateUserInfo(input: $input) {
      currentUser {
        id
        firstName
        lastName
        jobTitle
        about
        phone
      }
      errors {
        field
        code
        message
      }
    }
  }
`);
